import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link, useLocation } from "react-router-dom";
import { AlertTriangle, X, ChevronRight } from "lucide-react";

const HIGH_PRIORITY = new Set(["critical", "abnormal_vitals", "overdue_med", "stale_lab"]);

export default function GlobalAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [dismissedIds, setDismissedIds] = useState(() => {
    try { return new Set(JSON.parse(sessionStorage.getItem("nicu_dismissed_alerts") || "[]")); } catch { return new Set(); }
  });
  const [collapsed, setCollapsed] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await api.get("/dashboard/alerts");
        if (mounted) setAlerts(data.filter((a) => HIGH_PRIORITY.has(a.kind)));
      } catch (e) { console.warn("GlobalAlerts poll failed", e); }
    };
    load();
    const t = setInterval(load, 30_000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  const key = (a) => `${a.kind}:${a.patient_id || a.order_id || ""}:${a.text}`;
  const active = alerts.filter((a) => !dismissedIds.has(key(a)));

  const dismiss = (a) => {
    const k = key(a);
    setDismissedIds((s) => {
      const n = new Set(s); n.add(k);
      try { sessionStorage.setItem("nicu_dismissed_alerts", JSON.stringify([...n])); } catch (e) { console.warn("Persist dismissed alerts failed", e); }
      return n;
    });
  };

  // Hide on the login page (defensive)
  if (loc.pathname === "/login" || active.length === 0) return null;

  const tone = (kind) => {
    if (kind === "critical") return "bg-red-500 text-white";
    if (kind === "abnormal_vitals") return "bg-red-500/90 text-white";
    if (kind === "overdue_med") return "bg-amber-500 text-white";
    if (kind === "stale_lab") return "bg-amber-500/90 text-white";
    return "bg-primary text-primary-foreground";
  };

  if (collapsed) {
    return (
      <button
        onClick={() => setCollapsed(false)}
        data-testid="global-alerts-pill"
        className="mx-auto -mt-1 mb-2 inline-flex items-center gap-2 h-8 rounded-full bg-red-500 text-white px-3 text-xs shadow-lg hover:brightness-110"
      >
        <AlertTriangle size={12} /> {active.length} active alert{active.length > 1 ? "s" : ""}
        <ChevronRight size={12} />
      </button>
    );
  }

  return (
    <div className="w-full border-b border-red-500/25 bg-red-500/8" data-testid="global-alerts">
      <div className="max-w-[1400px] mx-auto flex items-start gap-2 px-4 lg:px-8 py-2">
        <div className="flex items-center gap-2 text-red-600 dark:text-red-400 pt-1 shrink-0">
          <AlertTriangle size={14} />
          <span className="text-[11px] uppercase tracking-widest font-semibold">Live alerts · {active.length}</span>
        </div>
        <div className="flex-1 flex flex-wrap gap-2 min-w-0">
          {active.slice(0, 6).map((a) => (
            <div key={key(a)} className={`inline-flex items-center gap-2 h-7 rounded-full px-3 text-xs font-medium ${tone(a.kind)}`}>
              {a.patient_id ? (
                <Link to={`/patients/${a.patient_id}`} className="hover:underline truncate max-w-[300px]">{a.text}</Link>
              ) : (
                <span className="truncate max-w-[300px]">{a.text}</span>
              )}
              <button onClick={() => dismiss(a)} className="opacity-70 hover:opacity-100" aria-label="Dismiss">
                <X size={11} />
              </button>
            </div>
          ))}
          {active.length > 6 && (
            <Link to="/dashboard" className="text-xs text-red-600 dark:text-red-400 hover:underline self-center">+{active.length - 6} more →</Link>
          )}
        </div>
        <button onClick={() => setCollapsed(true)} className="text-red-600 dark:text-red-400 self-center opacity-70 hover:opacity-100" aria-label="Collapse">
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
